import getNextObjectKey from 'services/numbers/getNextObjectKey';

import IDetail from '../IDetail';
import IInstallation from '../IInstallation';
import ISubproduct from '../ISubproduct';
import priceMaterialFromDetails from './subproducts/priceMaterialFromDetails';

/*
 * Rows deleted in the calculation window, kept while the window is open so they can be
 * restored to the place they were deleted from. Each row belongs to an alternative
 * (version) of the position.
 */
interface IDeletedRowBase {
  id: number;
  // The place of the row in its list when it was deleted
  index: number;
  versionIndex: number;
}

export type IDeletedRow =
  | (IDeletedRowBase & { kind: 'subproduct'; row: ISubproduct })
  | (IDeletedRowBase & { kind: 'installation'; row: IInstallation })
  | (IDeletedRowBase & {
      kind: 'detail';
      // The material row of the detail; found by its key and, after a save, by its name
      parentKey: number;
      parentName: string;
      row: IDetail;
    });

export const getSubproductName = (subproduct: ISubproduct): string =>
  (subproduct.isCustomSubproduct
    ? subproduct.customSubproduct
    : subproduct.product?.label) ?? '';

export const getInstallationName = (installation: IInstallation): string =>
  (installation.isCustomInstallation
    ? installation.customInstallation
    : installation.installation?.label) ?? '';

export const getDetailName = (detail: IDetail): string =>
  (detail.hasCustomProductName
    ? detail.customProductName
    : detail.product?.label) ?? '';

export const getDeletedRowName = (deletedRow: IDeletedRow): string => {
  if (deletedRow.kind === 'subproduct') {
    return getSubproductName(deletedRow.row);
  }

  if (deletedRow.kind === 'installation') {
    return getInstallationName(deletedRow.row);
  }

  return getDetailName(deletedRow.row);
};

// Inserts the item at its earlier place, or at the end when the list is now shorter
const insertAt = <T>(items: T[], index: number, item: T): T[] => {
  const place = Math.min(Math.max(index, 0), items.length);
  return [...items.slice(0, place), item, ...items.slice(place)];
};

export const restoreSubproduct = (
  subproducts: ISubproduct[],
  deletedRow: IDeletedRow & { kind: 'subproduct' }
): ISubproduct[] =>
  insertAt(subproducts, deletedRow.index, {
    ...deletedRow.row,
    key: getNextObjectKey(subproducts, 'key'),
  });

export const restoreInstallation = (
  installations: IInstallation[],
  deletedRow: IDeletedRow & { kind: 'installation' }
): IInstallation[] =>
  insertAt(installations, deletedRow.index, {
    ...deletedRow.row,
    key: getNextObjectKey(installations, 'key'),
  });

/**
 * Restores a detail to its material row. Returns null when the material row no longer
 * exists (it has to be restored first).
 */
export const restoreDetail = (
  subproducts: ISubproduct[],
  deletedRow: IDeletedRow & { kind: 'detail' }
): ISubproduct[] | null => {
  const parent =
    subproducts.find(
      (subproduct) =>
        subproduct.key === deletedRow.parentKey &&
        getSubproductName(subproduct) === deletedRow.parentName
    ) ??
    subproducts.find(
      (subproduct) => getSubproductName(subproduct) === deletedRow.parentName
    );

  if (parent === undefined) {
    return null;
  }

  const details = insertAt(parent.details, deletedRow.index, {
    ...deletedRow.row,
    key: getNextObjectKey(parent.details, 'key'),
  });

  // A price calculated from the details includes the restored detail again
  const restoredParent = priceMaterialFromDetails({ ...parent, details });

  return subproducts.map((subproduct) =>
    subproduct === parent ? restoredParent : subproduct
  );
};
